import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TeacherFilter from '../shared/teacher_filter';
import { FilterBar, LabeledSegmented, SelectFilter } from '../shared/controls';
import BulkModal from './bulk_modal';
import BulkSkip from '../shared/bulk_skip';
import FreePassBox from './free_pass_box';
import PenaltyTable from './penalty_table';
import { RowDialogs, row_dialog, useRowWrite } from './row_dialogs';
import { useRowRefocus } from './row_focus';
import { toggle_sort } from '../shared/use_sortable';
import {
  fetch_additional_sessions,
  fetch_additional_teachers,
  fetch_penalties_free_pass,
  fetch_penalties_list,
  fetch_penalties_summary,
  post_penalties_bulk
} from '../api';
import { api_error, is_permission_error } from '../format';
import { api_pay_month } from '../additional_payments/params';
import {
  append_page,
  bulk_selection,
  free_pass_view,
  has_more,
  list_view,
  other_month_text,
  selection_total_text,
  summary_view
} from './adapters';
import {
  BOOL_OPTIONS,
  BULK_ACTION,
  DIRECTIVE_ACTION,
  ERROR,
  PENALTY_TYPES,
  ROW_ACTION,
  SOURCE_OPTIONS,
  WAIVER_STATUS_OPTIONS,
  clear_pe_filters,
  filters_search,
  parse_filters,
  parse_list_sort,
  pe_filter_summary,
  pe_filters_on,
  set_pe_filter,
  set_pe_teacher
} from './params';
import { bulk_payload, free_pass_params, list_params, summary_params } from './requests';
import { linked_teacher_name, replace_search, teacher_picker_text } from '../shared/url_query';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { PENALTY_CSV_COLUMNS, penalty_csv_loader, penalty_csv_name } from './csv';
import './penalties.css';

/*
 * Every decision lives in params / requests / adapters / error_handlers; this file is the I/O
 * shell.
 */
const RESULT_BANNER = {
  success: 'tp-banner-ok',
  warning: 'tp-banner-warn',
  info: 'tp-banner-info'
};

const BULK_BAR_ID = 'tp-penalties-bulk';
const TYPE_OPTIONS = PENALTY_TYPES.map((t) => ({ value: t.penalty_type, label: t.label }));
const LIST_LOAD_FAILED_TEXT = 'Could not load the penalties.';
const SUMMARY_LOAD_FAILED_TEXT = 'Could not load the month totals.';
const FREE_PASS_LOAD_FAILED_TEXT = "Could not load this teacher's Free Passes.";

// Only a LOOKUP_FAILED message is an Ops sentence; the retry button beside it says "Try again".
const load_failed_text = (e, fallback) => {
  const { code, message } = api_error(e);
  return (code === ERROR.LOOKUP_FAILED && message.replace(/\s*try again\.?\s*$/i, '')) || fallback;
};

const PenaltiesTab = ({
  pay_month,
  month_label: month_text,
  month_badge,
  on_error,
  on_changed,
  new_request_id = uuidv4
}) => {
  const history = useHistory();
  const location = useLocation();
  const own_path = useRef(location.pathname);
  // The filters are kept in the URL, so a reload or a shared link returns to the same view; a month
  // change never re-reads it (the shell keys this tab by month: only the first mount reads the URL).
  const [filters, setFilters] = useState(() => parse_filters(location.search));
  // {key:'', dir:''} = the default id order (server keyset); a header click switches to a column sort.
  const [sort, setSort] = useState(() => parse_list_sort(location.search));
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));
  const [view, setView] = useState(() => list_view(null));
  const [list_state, setListState] = useState({ status: 'loading', error_text: '' });
  const [more_busy, setMoreBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summary_error, setSummaryError] = useState('');
  const [fp, setFp] = useState({ view: null, error_text: '' });
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [banner, setBanner] = useState('');
  const [highlight_uuid, setHighlight] = useState('');
  const seq = useRef(0);
  const scope_ref = useRef(null);
  const heading_ref = useRef(null);
  const refocus = useRowRefocus(scope_ref, heading_ref, view.rows);
  const month_closed = !!month_badge && month_badge.key === 'CLOSED';

  const fail_read = useCallback(
    (e) => {
      if (is_permission_error(e)) on_error(e);
    },
    [on_error]
  );

  // keep: a reload after a write leaves the rows on screen (and the opener's focus) until the new ones land.
  const load = useCallback(
    (keep = false) => {
      seq.current += 1;
      const mine = seq.current;
      if (!keep) setListState((s) => ({ ...s, status: 'loading' }));
      fetch_penalties_list(list_params(pay_month, { ...filters, sort }))
        .then((data) => {
          if (mine !== seq.current) return;
          setView(list_view(data));
          setListState({ status: 'ok', error_text: '' });
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          fail_read(e);
          setView(list_view(null));
          setListState({ status: 'error', error_text: load_failed_text(e, LIST_LOAD_FAILED_TEXT) });
        });
      fetch_penalties_summary(summary_params(pay_month))
        .then((data) => {
          if (mine !== seq.current) return;
          setSummary(summary_view(data));
          setSummaryError('');
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          fail_read(e);
          setSummary(null);
          setSummaryError(load_failed_text(e, SUMMARY_LOAD_FAILED_TEXT));
        });
      if (filters.teacher_uuid) {
        fetch_penalties_free_pass(free_pass_params(filters.teacher_uuid, pay_month))
          .then((data) => {
            if (mine === seq.current) setFp({ view: free_pass_view(data), error_text: '' });
          })
          .catch((e) => {
            if (mine !== seq.current) return;
            fail_read(e);
            setFp({ view: null, error_text: load_failed_text(e, FREE_PASS_LOAD_FAILED_TEXT) });
          });
      } else {
        setFp({ view: null, error_text: '' });
      }
    },
    [pay_month, filters, sort, fail_read]
  );

  useEffect(() => {
    load();
  }, [load]);

  // The URL mirrors the applied filters and sort (replace, not push: they are not history steps),
  // merged into the current search so the shell's own ?month= (or any other param) is never clobbered.
  useEffect(() => {
    // Never rewrite another page's URL (a push away lands before this tab unmounts).
    replace_search(history, own_path.current, (search) => filters_search(search, filters, sort));
  }, [filters, sort, history, location.pathname, location.search]);

  const load_more = () => {
    if (more_busy || !has_more(view)) return;
    const mine = seq.current;
    setMoreBusy(true);
    // A column sort pages by next_offset; the default id order pages by next_after_id (never both).
    const page =
      view.next_offset !== null ? { offset: view.next_offset } : { after_id: view.next_after_id };
    fetch_penalties_list(list_params(pay_month, { ...filters, sort }, page))
      .then((data) => {
        if (mine === seq.current) setView((v) => append_page(v, list_view(data)));
      })
      .catch((e) => {
        fail_read(e);
        if (mine === seq.current) setBanner(load_failed_text(e, LIST_LOAD_FAILED_TEXT));
      })
      .finally(() => setMoreBusy(false));
  };

  // Each change applies at once; the load effect refetches from the first page. Picks are cleared
  // so a bulk action never reaches rows no longer shown.
  const run_filters = (next) => {
    setSelected({});
    setFilters(next);
  };
  const set_filter = (key, value) => run_filters(set_pe_filter(filters, key, value));

  const focus_heading = () => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  };

  // A write's result lands in the tab banner; a refusal stays in the dialog that sent it.
  const w = useRowWrite({
    on_saved: (r) => {
      setResult({ ...r, other_month_text: other_month_text(r.penalty, pay_month) });
      setBanner('');
      setHighlight(r.penalty && !r.removed ? r.penalty.uuid : '');
      refocus.arm();
      load(true);
      on_changed();
    },
    on_reload: () => load(true),
    on_permission: on_error
  });

  // row: the penalty the pass is applied from, so the dialog can say a pass covers it.
  const open_pass = (action, teacher_uuid, teacher_name, day, row = null) => {
    setBanner('');
    fetch_penalties_free_pass(free_pass_params(teacher_uuid, pay_month))
      .then((data) =>
        w.open({ kind: 'pass', action, state: free_pass_view(data), day, row, teacher_name })
      )
      .catch((e) => {
        fail_read(e);
        setBanner(load_failed_text(e, FREE_PASS_LOAD_FAILED_TEXT));
      });
  };

  const on_row_action = (action, row) => {
    refocus.remember(row.uuid);
    const next = row_dialog(action, row);
    if (next) w.open(next);
    else if (action === ROW_ACTION.APPLY_PASS) {
      open_pass(DIRECTIVE_ACTION.APPLY, row.teacher_uuid, row.teacher_name, row.day_ist, row);
    } else if (action === ROW_ACTION.REMOVE_PASS) {
      open_pass(DIRECTIVE_ACTION.REMOVE, row.teacher_uuid, row.teacher_name, row.day_ist);
    }
  };

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );
  // month: the Add dialog's own month ("YYYY-MM"), which a move can change.
  const search_sessions = useCallback(
    (teacher_uuid, q, month) =>
      teacher_uuid
        ? fetch_additional_sessions({ teacher_uuid, pay_month: api_pay_month(month), q }).then(
            (d) => (Array.isArray(d.rows) ? d.rows : [])
          )
        : Promise.resolve([]),
    []
  );

  const selected_rows = view.rows.filter((r) => selected[r.uuid]);
  const toggle = (r) => setSelected((s) => ({ ...s, [r.uuid]: !s[r.uuid] }));
  const toggle_all = (on) =>
    setSelected(on ? Object.fromEntries(view.rows.map((r) => [r.uuid, true])) : {});
  const open_bulk = (action) => {
    const { eligible, skipped } = bulk_selection(selected_rows, action);
    w.open({ kind: 'bulk', action, rows: eligible, skipped });
  };

  const add_off = month_closed;
  // A linked teacher arrives as a uuid only; the name is read back from the listed rows.
  const teacher_name = filters.teacher_name || linked_teacher_name(view.rows, filters.teacher_uuid);
  const filter_summary =
    list_state.status === 'ok'
      ? pe_filter_summary({
          count: view.rows.length,
          more: has_more(view),
          month_label: month_text,
          filters: { ...filters, teacher_name },
          loading: false
        })
      : null;

  return (
    <div className="tp-pe" ref={scope_ref}>
      {month_closed ? (
        <div className="tp-banner tp-banner-info">
          {month_text} is closed for every teacher. Reopen a teacher&apos;s month under Needs
          attention to change its penalties.
        </div>
      ) : null}
      {summary_error ? (
        <LoadFailed text={summary_error} on_retry={() => load()} />
      ) : summary ? (
        <>
          {/* One tile grid for both groups: the lead total spans two, the per-type tiles follow. */}
          <div className="tp-stats-wrap">
            <div className="tp-stats tp-stats-4">
              {summary.totals.map((c) => (
                <div key={c.key} className={`tp-stat${c.key === 'charged' ? ' tp-stat-lead' : ''}`}>
                  <div className="tp-stat-label">{c.label}</div>
                  <div className={`tp-stat-value${c.tone === 'neg' ? ' tp-neg' : ''}`}>
                    {c.value}
                  </div>
                  <span className="tp-stat-chip">{c.sub}</span>
                </div>
              ))}
              {summary.cards.map((c) => (
                <div key={c.key} className="tp-stat">
                  <div className="tp-stat-label">{c.label}</div>
                  <div className="tp-stat-value">{c.value}</div>
                  <span className="tp-stat-chip">{c.sub}</span>
                  {c.breakdown.length ? (
                    <div className="tp-stat-meta">
                      {c.breakdown.map((b) => (
                        <span key={b.label}>
                          {b.label} <b>{b.value}</b>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {result ? (
        <div className={`tp-banner ${RESULT_BANNER[result.tone]}`} role="status">
          {result.message}
          {result.warnings.map((w) => (
            <div key={w.code}>{w.message}</div>
          ))}
          {result.issues_text ? <div>{result.issues_text}</div> : null}
          {result.other_month_text ? <div>{result.other_month_text}</div> : null}
        </div>
      ) : null}
      {banner ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {banner}
        </div>
      ) : null}

      <FilterBar
        label="Penalty filters"
        summary={
          filter_summary ? (
            <>
              Showing <b>{filter_summary.lead}</b>
              {filter_summary.rest}
            </>
          ) : null
        }
      >
        <div className="tp-fsearch tp-pe-filter-teacher">
          <TeacherFilter
            id="tp-pe-filter-teacher"
            label="Teacher"
            value={{
              teacher_uuid: filters.teacher_uuid,
              display: teacher_picker_text(filters.teacher_uuid, teacher_name)
            }}
            onChange={(next) => run_filters(set_pe_teacher(filters, next))}
          />
        </div>
        <SelectFilter
          id="tp-pe-filter-penalty-type"
          label="Type"
          value={filters.penalty_type}
          all_label="All types"
          options={TYPE_OPTIONS}
          onChange={(v) => set_filter('penalty_type', v)}
        />
        <LabeledSegmented
          label="Status"
          options={[{ value: '', label: 'Any' }, ...WAIVER_STATUS_OPTIONS]}
          value={filters.waiver_status}
          onChange={(v) => set_filter('waiver_status', v)}
        />
        <LabeledSegmented
          label="Source"
          options={[{ value: '', label: 'Any' }, ...SOURCE_OPTIONS]}
          value={filters.source}
          onChange={(v) => set_filter('source', v)}
        />
        <LabeledSegmented
          label="Changed by Ops"
          options={[{ value: '', label: 'Any' }, ...BOOL_OPTIONS]}
          value={filters.overridden}
          onChange={(v) => set_filter('overridden', v)}
        />
        <LabeledSegmented
          label="Orphaned"
          options={[{ value: '', label: 'Any' }, ...BOOL_OPTIONS]}
          value={filters.orphaned}
          onChange={(v) => set_filter('orphaned', v)}
        />
        {pe_filters_on(filters) ? (
          <button
            type="button"
            className="tp-link tp-fbar-end"
            onClick={() => run_filters(clear_pe_filters())}
          >
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      {filters.teacher_uuid && fp.error_text ? (
        <LoadFailed text={fp.error_text} on_retry={() => load()} />
      ) : null}
      {filters.teacher_uuid && fp.view ? (
        <FreePassBox
          view={fp.view}
          teacher_name={teacher_name}
          on_action={(action, day) => {
            refocus.remember('');
            w.open({
              kind: 'pass',
              action,
              state: fp.view,
              day,
              teacher_name
            });
          }}
          on_month_pass={(action) => {
            refocus.remember('');
            w.open({
              kind: 'month_pass',
              action,
              teacher_uuid: fp.view.raw.teacher_uuid,
              pay_month: fp.view.raw.pay_month,
              teacher_name,
              month_text: fp.view.month_text
            });
          }}
        />
      ) : null}

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title" ref={heading_ref} tabIndex={-1}>
              Penalties · {month_text}
            </span>
            <span className="tp-card-hd-sub">
              {list_state.status === 'ok'
                ? `${view.rows.length} shown${has_more(view) ? ', more to load' : ''}`
                : ''}
            </span>
          </span>
          <span className="tp-card-hd-acts">
            <button
              type="button"
              className="tp-btn"
              onClick={add_off ? undefined : () => w.open({ kind: 'add' })}
              aria-disabled={add_off ? 'true' : undefined}
              title={add_off ? `${month_text} is closed.` : ''}
            >
              + Add penalty
            </button>
            <DownloadCsvButton
              columns={PENALTY_CSV_COLUMNS}
              load_rows={penalty_csv_loader(fetch_penalties_list, pay_month, filters, sort)}
              file_name={() => penalty_csv_name(pay_month)}
              disabled={list_state.status !== 'ok'}
              on_error={fail_read}
            />
          </span>
        </div>
        <BulkSkip count={selected_rows.length} target_id={BULK_BAR_ID} />
        {list_state.status === 'loading' ? <CustomLoading /> : null}
        {list_state.status === 'error' ? (
          <LoadFailed text={list_state.error_text} on_retry={() => load()} />
        ) : null}
        {list_state.status === 'ok' ? (
          <PenaltyTable
            rows={view.rows}
            selected={selected}
            on_toggle={toggle}
            on_toggle_all={toggle_all}
            on_action={on_row_action}
            highlight_uuid={highlight_uuid}
            empty_text={`No penalties in ${month_text} for these filters.`}
            sort={sort}
            on_sort={on_sort}
            on_sort_set={setSort}
            on_error={on_error}
          />
        ) : null}
      </div>
      {list_state.status === 'ok' && has_more(view) ? (
        <div className="tp-pe-more">
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={more_busy ? undefined : load_more}
            aria-disabled={more_busy ? 'true' : undefined}
          >
            {more_busy ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
      {/* After the list and Load more: sticky at the viewport bottom while rows are selected, and
          never covering the last row once scrolled to the end. */}
      {selected_rows.length ? (
        <div
          id={BULK_BAR_ID}
          tabIndex={-1}
          className="tp-bulkbar tp-bulkbar-sticky"
          role="region"
          aria-label="Selected penalties"
        >
          <span className="tp-bulkbar-sum">
            <b>{selected_rows.length} selected</b>
            <span className="tp-bulkbar-k">Charged</span>
            <span className="tp-bulkbar-amt tp-neg">{selection_total_text(selected_rows)}</span>
          </span>
          <button type="button" className="tp-btn" onClick={() => open_bulk(BULK_ACTION.WAIVE)}>
            Waive selected
          </button>
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => open_bulk(BULK_ACTION.APPLY_FREE_PASS)}
          >
            Use Free Passes
          </button>
          <button type="button" className="tp-link" onClick={() => setSelected({})}>
            Clear
          </button>
        </div>
      ) : null}

      <RowDialogs
        w={w}
        pay_month={pay_month}
        month_text={month_text}
        add={{
          new_request_id,
          search_teachers,
          search_sessions,
          // the live policy map, or null while the summary hasn't loaded (the Add form leaves %
          // empty).
          default_pcts: summary ? summary.default_pcts : null
        }}
        pass={{
          load: (row) =>
            fetch_penalties_free_pass(free_pass_params(row.teacher_uuid, pay_month))
              .then(free_pass_view)
              .catch((e) => {
                fail_read(e);
                throw new Error(load_failed_text(e, FREE_PASS_LOAD_FAILED_TEXT));
              })
        }}
        on_focus_lost={focus_heading}
      />
      {w.dialog && w.dialog.kind === 'bulk' ? (
        <BulkModal
          action={w.dialog.action}
          rows={w.dialog.rows}
          skipped={w.dialog.skipped}
          send={(rows, form, confirm_displace) =>
            post_penalties_bulk(bulk_payload(w.dialog.action, rows, form, confirm_displace))
          }
          on_finished={() => {
            setSelected({});
            load(true);
            on_changed();
          }}
          on_permission={on_error}
          on_close={w.close}
          on_focus_lost={focus_heading}
        />
      ) : null}
    </div>
  );
};

export default PenaltiesTab;
